import { useTranslation } from 'react-i18next'
import { ExtendedSearchFilters } from './SearchFiltersContext'
import { X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { fetchPractices } from '../../services/practices'

interface ActiveSearchFiltersProps {
  filters: ExtendedSearchFilters
  onRemoveFilter: (type: string, value?: string) => void
}

export const ActiveSearchFilters: React.FC<ActiveSearchFiltersProps> = ({
  filters,
  onRemoveFilter,
}) => {
  const { t } = useTranslation()
  const [practicesMap, setPracticesMap] = useState<Map<number, string>>(
    new Map(),
  )

  useEffect(() => {
    const loadPractices = async () => {
      try {
        const categories = await fetchPractices()
        const practiceMapping = new Map<number, string>()

        categories.forEach((category: { practices: any[] }) => {
          category.practices.forEach(
            (practice: { id: number; practiceName: string }) => {
              practiceMapping.set(practice.id, practice.practiceName)
            },
          )
        })

        setPracticesMap(practiceMapping)
      } catch (error) {
        console.error('Failed to load practices:', error)
      }
    }

    loadPractices()
  }, [])

  const noTranslationNeeded = [
    'distance',
    'penisSize',
    'location',
    'ageRange',
    'sizeRange',
    'weightRange',
  ]

  const getActiveFilters = () => {
    const storedFilters = JSON.parse(
      localStorage.getItem('bigSearchFilters') || '{}',
    )

    return Object.entries(storedFilters)
      .filter(([key, value]) => {
        if (key === 'rememberFilters') return false
        if (Array.isArray(value)) return value.length > 0
        if (typeof value === 'boolean') return value !== null
        return value !== null && value !== undefined
      })
      .map(([key, value]) => {
        let displayValue = ''

        if (Array.isArray(value)) {
          if (key === 'practices') {
            displayValue = value
              .map(practiceId => {
                const practiceName = practicesMap.get(Number(practiceId))
                return practiceName
                  ? t(`practices.${practiceName}`)
                  : practiceId
              })
              .join(', ')
          } else if (key === 'nationality') {
            displayValue = value
              .map(val => t(`nationalities.${val}`))
              .join(', ')
          } else if (key === 'languages') {
            displayValue = value.map(val => t(`languages.${val}`)).join(', ')
          } else if (noTranslationNeeded.includes(key)) {
            displayValue = value.join(', ')
          } else {
            displayValue = value.map(val => t(`create-ad.${val}`)).join(', ')
          }
        } else if (typeof value === 'boolean') {
          displayValue = value ? t('yes') : t('no')
        }

        return {
          type: key,
          value: displayValue,
          needsTranslation:
            !noTranslationNeeded.includes(key) && key !== 'practices',
        }
      })
  }

  const activeFilters = getActiveFilters()

  if (activeFilters.length === 0) return null

  return (
    <div className='mb-5 px-2 max-w-full'>
      <div className='rounded-md bg-grisclair px-2'>
        <div className='flex flex-col lg:flex-row items-start sm:items-center text-xs xl:text-sm'>
          <div className='flex flex-wrap gap-2 py-2'>
            <div className='bg-blue-500 text-white pl-3 pr-2 py-1 rounded-md flex items-center gap-2 font-medium group'>
              <div className='flex items-center gap-1 '>
                <span className='font-medium'>{t('active-filters')}</span>
              </div>
            </div>
            {activeFilters.map(filter => (
              <div
                key={filter.type}
                className='bg-gray-200 pl-3 pr-2 py-1 rounded-md text-anthracite flex items-center gap-2 font-medium group'
              >
                <div className='flex items-center gap-1'>
                  <span className='font-medium'>
                    {t(`create-ad.${filter.type}`)}
                  </span>
                  <span className='text-redpink'>
                    {filter.type === 'practices' ||
                    filter.needsTranslation === false
                      ? filter.value
                      : filter.value}
                  </span>
                </div>
                <button
                  onClick={() => onRemoveFilter(filter.type)}
                  className='w-5 h-5 rounded flex items-center justify-center hover:bg-gray-300 transition-colors'
                  title={t('remove-filter')}
                >
                  <X className='w-3 h-3 text-anthracite' />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
